'use client';

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getCountryCallingCode,
  parsePhoneNumber,
  validatePhoneNumberLength,
  type CountryCode,
} from 'libphonenumber-js';
import { PiPlus, PiWarning, PiX } from 'react-icons/pi';
import {
  cn,
  formatPhoneNumber,
  getDefaultCountryFromLocale,
} from '@ais-ui/utils';
import { Badge } from './Badge';
import { Button } from './Button';
import { inputStyles } from './Input';
import {
  CountryCodeSelect,
  CountryFlag,
  PHONE_COUNTRIES,
} from './CountryCodeSelect';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './Tooltip';

type PhoneListInputProps = {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  disabled?: boolean;
  disabledReason?: string;
  /** Forwarded by `FormControl` so the label/description are associated
   * with the actual number input rather than being silently dropped. */
  id?: string;
  name?: string;
  onBlur?: () => void;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean | 'true' | 'false';
};

interface ParsedChip {
  raw: string;
  iso?: CountryCode;
  display: string;
  unusualLength: boolean;
}

function parseChip(raw: string): ParsedChip {
  // `parsePhoneNumber` (like the `formatPhoneNumber` display helper it
  // replaces) requires a leading "+" to identify a country, so legacy
  // values stored without one render as a plain, unflagged chip. This is
  // the same graceful degradation the app had before this component existed
  // — nothing new to migrate, just nothing to enrich either.
  try {
    const parsed = parsePhoneNumber(raw);
    if (parsed?.country) {
      const lengthResult = validatePhoneNumberLength(
        parsed.nationalNumber,
        parsed.country,
      );
      return {
        raw,
        iso: parsed.country,
        display: formatPhoneNumber(raw),
        unusualLength:
          lengthResult === 'TOO_SHORT' ||
          lengthResult === 'TOO_LONG' ||
          lengthResult === 'INVALID_LENGTH',
      };
    }
  } catch {
    // Not parseable — render the raw value without country context below.
  }
  return { raw, display: formatPhoneNumber(raw), unusualLength: false };
}

/**
 * Country-aware replacement for the plain-text phone `ListInput`. Same
 * external contract (`value`/`onChange`/`placeholder`/`disabled`/
 * `disabledReason`) so it drops straight into an existing `FormField`.
 *
 * Renders a split field — country picker, national-number input, Add
 * button — and stores each added number as an E.164 string (e.g.
 * `+4512345678`), identical to what `ListInput` stored before. Numbers with
 * an unusual length for their country are flagged with a warning but are
 * never blocked from being added or submitted.
 */
export const PhoneListInput = React.forwardRef<
  HTMLDivElement,
  PhoneListInputProps
>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled = false,
      disabledReason = '',
      id,
      name,
      onBlur,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
    },
    ref,
  ) => {
    const [country, setCountry] = useState<CountryCode>('DK');
    const [numberInput, setNumberInput] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    // Preselect the country from the browser locale once on mount — no
    // permission prompt, no network call (see getDefaultCountryFromLocale).
    useEffect(() => {
      setCountry(getDefaultCountryFromLocale());
    }, []);

    useEffect(() => {
      if (disabled) setNumberInput('');
    }, [disabled]);

    const handleAdd = useCallback(() => {
      if (disabled) return;
      const digits = numberInput.replace(/\D/g, '');
      if (!digits) return;

      // Parse against the selected country so national trunk prefixes
      // (e.g. a leading "0" in many European national formats) are
      // stripped correctly instead of being baked into the E.164 value.
      let e164: string;
      try {
        const parsed = parsePhoneNumber(digits, country);
        e164 = parsed.number;
      } catch {
        // Not parseable (e.g. too short/long for libphonenumber to even
        // attempt) — fall back to naive concatenation so input is never lost.
        const dialCode = getCountryCallingCode(country);
        e164 = `+${dialCode}${digits}`;
      }
      onChange(value ? [...value, e164] : [e164]);
      setNumberInput('');
    }, [country, numberInput, value, onChange, disabled]);

    const handleNumberChange = useCallback((raw: string) => {
      // Paste-assist: if the user pastes/types a full international number,
      // switch the country picker to match and keep just the national part.
      if (raw.startsWith('+')) {
        try {
          const parsed = parsePhoneNumber(raw);
          if (parsed?.country) {
            setCountry(parsed.country);
            setNumberInput(parsed.nationalNumber);
            return;
          }
        } catch {
          // Not parseable yet (user is still typing) — keep the raw input.
        }
      }
      setNumberInput(raw);
    }, []);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          handleAdd();
        }
        if (e.key === 'Backspace' && numberInput === '' && value.length > 0) {
          onChange(value.slice(0, -1));
        }
      },
      [handleAdd, numberInput, value, onChange],
    );

    const handleRemove = useCallback(
      (raw: string) => {
        if (disabled) return;
        onChange(value.filter((v) => v !== raw));
      },
      [value, onChange, disabled],
    );

    // Mirrors `ListInput`'s blur-to-add behavior (tabbing/clicking away
    // commits a pending number instead of silently discarding it), while
    // still calling through to react-hook-form's own `onBlur` so touched
    // state and blur-triggered validation keep working.
    const handleInputBlur = useCallback(() => {
      handleAdd();
      onBlur?.();
    }, [handleAdd, onBlur]);

    return (
      <div ref={ref}>
        {/*
          Each segment has its own individual shadow removed (`shadow-none`) so
          the row reads as one unified control rather than three separately
          elevated pieces — the `shadow-xs` below on the wrapper supplies the
          single, consistent elevation shared with plain inputs/buttons
          elsewhere in the app.
        */}
        <div className="flex items-stretch rounded-md shadow-xs">
          <CountryCodeSelect
            value={country}
            onChange={setCountry}
            disabled={disabled}
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="flex-1 min-w-0"
                  tabIndex={disabled && disabledReason ? 0 : undefined}
                  aria-disabled={disabled}
                >
                  <input
                    ref={inputRef}
                    id={id}
                    name={name}
                    type="tel"
                    inputMode="tel"
                    disabled={disabled}
                    value={numberInput}
                    onChange={(e) => handleNumberChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleInputBlur}
                    placeholder={placeholder}
                    aria-describedby={ariaDescribedBy}
                    aria-invalid={ariaInvalid}
                    className={cn(
                      inputStyles,
                      'rounded-none shadow-none tabular-nums',
                    )}
                  />
                </div>
              </TooltipTrigger>
              {disabled && disabledReason && (
                <TooltipContent>
                  <p>{disabledReason}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            onClick={handleAdd}
            className="rounded-l-none -ml-px flex-none shadow-none"
          >
            <PiPlus />
            Add
          </Button>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((raw) => {
            const chip = parseChip(raw);
            const countryName = chip.iso
              ? (PHONE_COUNTRIES.find((c) => c.iso === chip.iso)?.name ??
                chip.iso)
              : undefined;

            return (
              <Badge key={raw} variant="secondary" className="gap-1.5 pr-1">
                {chip.iso && (
                  <CountryFlag
                    iso={chip.iso}
                    className="w-[15px] h-[11px] flex-none"
                  />
                )}
                {chip.iso && (
                  <span className="text-[10px] font-semibold text-muted-foreground">
                    {chip.iso}
                  </span>
                )}
                <span className="tabular-nums">{chip.display}</span>
                {chip.unusualLength && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <PiWarning
                          className="size-3 text-amber-500 flex-none"
                          aria-label={`Unusual length for ${countryName}`}
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Unusual length for {countryName}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${chip.display}`}
                    className="ml-0.5 rounded-full outline-none"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={() => handleRemove(raw)}
                  >
                    <PiX className="size-3 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </Badge>
            );
          })}
        </div>
      </div>
    );
  },
);
PhoneListInput.displayName = 'PhoneListInput';
